import { useEffect, useRef, type ReactNode } from "react";
import { useKeycloak } from "./keycloakContext";
import { getAccessToken, redirectToPlatformLogin } from "./tokenManager";
import { logKeycloakToken, tokenDebugInfo } from "./authDebugLog";

interface AuthGateProps {
  children: ReactNode;
}

/** MantineProvider 없이도 렌더 가능한 최소 로더 (AuthGate는 ThemeProvider 밖에서 동작). */
function AuthLoading() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh"
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          border: "3px solid #e0e0e0",
          borderTopColor: "#f37321",
          borderRadius: "50%",
          animation: "auth-gate-spin 0.8s linear infinite"
        }}
      />
      <style>{`@keyframes auth-gate-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

/**
 * keycloak.init() 완료 후 인증 게이트.
 *
 * check-sso 완료 후 authenticated=false 이면 keycloak.login() 을 호출한다.
 * - 이미 SSO 세션이 있으면(Platform 에서 돌아온 경우) Keycloak 이 즉시 auth code 와 함께 redirect.
 * - SSO 세션이 없으면 Keycloak 로그인 페이지가 표시된다.
 *
 * check-sso iframe 은 localhost 에서 3rd-party 쿠키 차단으로 실패할 수 있어,
 * keycloak.login() 으로 full-page redirect 를 수행해야 SSO 세션을 감지할 수 있다.
 */
export function AuthGate({ children }: AuthGateProps) {
  const { keycloak, initialized } = useKeycloak();
  const loginCalledRef = useRef(false);

  useEffect(() => {
    if (!initialized) return;
    logKeycloakToken("AuthGate.initComplete", {
      authenticated: keycloak.authenticated,
      keycloakToken: tokenDebugInfo(keycloak.token),
      memoryToken: tokenDebugInfo(getAccessToken())
    });

    if (!keycloak.authenticated && !loginCalledRef.current) {
      loginCalledRef.current = true;
      redirectToPlatformLogin();
    }
  }, [initialized, keycloak.authenticated, keycloak.token]);

  if (!initialized || !keycloak.authenticated) {
    return <AuthLoading />;
  }

  return children;
}
